import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ThemedCard } from '../components/themed';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { logger } from '../lib/utils/logger';
import { H1 } from '../components/Heading';
import { APP_ROUTES } from '../config/routes';
import { userApiClient } from '../lib/api/client';

interface FinalizeAfterVerifyResponse {
  success: boolean;
  email: string | null;
}

/**
 * VerifyEmail page — handles the Supabase email-verification redirect.
 *
 * Flow:
 * 1. User clicks verification link in email
 * 2. Supabase validates the token and sets email_confirmed_at
 * 3. Supabase redirects here with access_token / refresh_token in the URL hash
 * 4. We POST the access_token to /api/v1/auth/finalize-after-verify so the
 *    backend can run signup_contexts routing (create org / approve invitation
 *    / record self-enrollment) before the user logs in
 * 5. Redirect to /login with ?email=... so the form is pre-filled
 *
 * The user still needs to type their password — they just typed it at signup,
 * so this is intentional and matches the rest of the auth flow.
 */
const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Supabase sends params in hash fragment (#) not query params (?)
        // Parse hash fragment: #access_token=...&refresh_token=...&type=signup
        const hash = window.location.hash.substring(1); // Remove leading #
        const hashParams = new URLSearchParams(hash);

        // Also check query params as fallback
        const error = hashParams.get('error') || searchParams.get('error');
        const errorDescription =
          hashParams.get('error_description') || searchParams.get('error_description');

        if (error) {
          logger.error('Email verification error:', error, errorDescription);
          setStatus('error');
          setMessage(errorDescription || 'Email verification failed. The link may have expired.');
          return;
        }

        const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');
        const type = hashParams.get('type') || searchParams.get('type');

        const hasSuccessIndicators = accessToken || refreshToken || type === 'signup';

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!hasSuccessIndicators) {
          logger.warn('Verify email page loaded without verification params');
          setStatus('error');
          setMessage('Invalid verification link. Please request a new verification email.');
          return;
        }

        logger.info('Email verified — finalizing signup');
        setMessage('Email verified! Finalizing your account...');

        // Best-effort finalization. If this fails the next login will retry
        // (the handlers are idempotent), so we still proceed to the login page.
        let prefillEmail: string | null = null;
        if (accessToken !== null && accessToken !== '') {
          try {
            const data = await userApiClient.post<FinalizeAfterVerifyResponse>(
              '/api/v1/auth/finalize-after-verify',
              { accessToken },
              { skipAuth: true },
            );
            prefillEmail = data.email;
          } catch (finalizeError) {
            logger.warn('finalize-after-verify failed; continuing to login', finalizeError);
          }
        }

        setStatus('success');
        setMessage('Account ready! Redirecting to sign in...');

        setTimeout(() => {
          const loginPath =
            prefillEmail !== null && prefillEmail !== ''
              ? `${APP_ROUTES.LOGIN}?email=${encodeURIComponent(prefillEmail)}`
              : APP_ROUTES.LOGIN;
          navigate(loginPath, { replace: true });
        }, 1500);
      } catch (error) {
        logger.error('Error processing email verification:', error);
        setStatus('error');
        setMessage('An error occurred during verification. Please try again.');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center surface-secondary">
      <ThemedCard className="w-96">
        <div className="p-6">
          {status === 'verifying' && (
            <>
              <div className="flex justify-center mb-4">
                <Loader2 className="h-16 w-16 text-sky-600 dark:text-sky-400 animate-spin" />
              </div>
              <H1 className="text-center mb-2">Verifying Email</H1>
              <p className="text-sm text-center text-secondary">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-700 dark:text-green-400" />
                </div>
              </div>
              <H1 className="text-center mb-2">Email Verified!</H1>
              <p className="text-sm text-center text-secondary mb-4">{message}</p>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-sm text-green-800 dark:text-green-200">
                  Your account has been verified. Sign in with your password to continue.
                </p>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <H1 className="text-center mb-2">Verification Failed</H1>
              <p className="text-sm text-center text-secondary mb-6">{message}</p>

              <div className="space-y-3">
                <button
                  onClick={() => navigate(APP_ROUTES.LOGIN)}
                  className="w-full px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white font-medium rounded-lg transition-colors"
                >
                  Go to Login
                </button>
                <div className="text-center">
                  <Link
                    to={APP_ROUTES.SIGNUP}
                    className="text-sm text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                  >
                    Sign up again
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </ThemedCard>
    </div>
  );
};

export default VerifyEmail;
